import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button } from '@thegpvc/ui';
import { api } from '../api/client';
import { CopyButton } from './CopyButton';
import { SectionCard } from './SectionCard';
import { cx, text } from '../styles/theme';
import { errorMessage } from '../utils/errorMessage';
import type { ConstraintCandidate } from '../types/api';

const SEVERITY_VARIANT: Record<string, 'error' | 'warning' | 'neutral'> = {
  high: 'error',
  medium: 'warning',
  low: 'neutral',
};

function CandidateCard({
  candidate,
  onDismiss,
  pending,
}: {
  candidate: ConstraintCandidate;
  onDismiss: (clusterKey: string) => void;
  pending: boolean;
}) {
  const evidence = candidate.evidence ?? [];
  const projects = candidate.projectSpread ?? [];
  const sessionWord = candidate.sessionCount === 1 ? 'session' : 'sessions';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800/40">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={SEVERITY_VARIANT[candidate.severity] ?? 'neutral'}>{candidate.severity}</Badge>
            <h3 className={cx('text-base font-semibold', text.primary)}>{candidate.title}</h3>
          </div>
          <p className={cx('text-sm', text.secondary)}>{candidate.detail}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={pending}
          onClick={() => onDismiss(candidate.clusterKey)}
          className={text.secondary}
        >
          Dismiss
        </Button>
      </div>

      {/* Copy-paste candidate line — the whole point of this surface. */}
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-sm dark:border-navy-700 dark:bg-navy-900/40">
        <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-words">{candidate.candidateLine}</pre>
        <CopyButton value={candidate.candidateLine} label="Copy candidate line" />
      </div>

      <p className="mt-3 text-sm">
        <span className={cx('font-medium', text.primary)}>Try: </span>
        <span className={text.secondary}>{candidate.recommendation}</span>
      </p>

      <div className={cx('mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs', text.muted)}>
        <span>
          Re-explained across <span className="tabular-nums font-medium">{candidate.sessionCount}</span> {sessionWord}
        </span>
        {projects.length > 0 && (
          <span className="flex flex-wrap items-center gap-1">
            {projects.map((p) => (
              <span
                key={p}
                className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-navy-600 dark:bg-navy-700 dark:text-navy-200"
              >
                {p}
              </span>
            ))}
          </span>
        )}
      </div>

      {evidence.length > 0 && (
        <details className="mt-3">
          <summary className={cx('cursor-pointer text-xs font-medium', text.link)}>
            Why — {evidence.length} {evidence.length === 1 ? 'example' : 'examples'}
          </summary>
          <ul className="mt-2 space-y-2">
            {evidence.map((e, i) => (
              <li key={i} className="border-l-2 border-gray-200 pl-3 dark:border-navy-700">
                {(e.project || e.sessionId) && (
                  <div className={cx('flex items-center gap-2 text-xs', text.muted)}>
                    {e.project && <span>{e.project}</span>}
                    {e.sessionId && (
                      <Link to={`/sessions/${e.sessionId}`} className={cx('font-mono', text.link)}>
                        {e.sessionId.slice(0, 8)}…
                      </Link>
                    )}
                  </div>
                )}
                <p className={cx('text-sm italic', text.secondary)}>&ldquo;{e.promptText}&rdquo;</p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * ConstraintCandidates is the "direction" coaching axis: recurring constraints
 * the operator keeps re-explaining, surfaced as paste-ready CLAUDE.md/AGENTS.md
 * lines with evidence drill-down and a persistent dismiss action. Sits alongside
 * the execution-axis coaching on the Insights page, user-scoped rather than
 * session-scoped.
 *
 * When there are no (or all-dismissed) candidates it renders an explicit
 * "No recurring constraints found" message rather than a blank card.
 */
export function ConstraintCandidates() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['constraintCandidates'],
    queryFn: () => api.listConstraintCandidates(),
  });

  const dismiss = useMutation({
    mutationFn: (clusterKey: string) => api.dismissConstraintCandidate(clusterKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['constraintCandidates'] }),
  });

  const candidates = data?.candidates ?? [];

  return (
    <SectionCard title="Constraints to codify">
      <p className={cx('text-sm', text.secondary)}>
        Rules you keep re-explaining across sessions — paste them into CLAUDE.md so you only say them once.
      </p>

      {dismiss.isError && (
        <Alert variant="error">Failed to dismiss candidate: {errorMessage(dismiss.error)}</Alert>
      )}
      {error && <Alert variant="error">Failed to load constraint candidates: {errorMessage(error)}</Alert>}

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg bg-gray-100 dark:bg-navy-800" />
          ))}
        </div>
      )}

      {!isLoading && !error && candidates.length === 0 && (
        <p className={cx('py-8 text-center text-sm', text.secondary)}>
          No recurring constraints found. They emerge once you re-explain the same rule across multiple sessions.
        </p>
      )}

      {!isLoading && candidates.length > 0 && (
        <div className="space-y-3">
          {candidates.map((c) => (
            <CandidateCard
              key={c.clusterKey}
              candidate={c}
              pending={dismiss.isPending && dismiss.variables === c.clusterKey}
              onDismiss={(clusterKey) => dismiss.mutate(clusterKey)}
            />
          ))}
        </div>
      )}
    </SectionCard>
  );
}
